import axios from 'axios'
import { set } from 'lodash'
import stripe from '@shared/Stripe'

const INPUT_CHANGED = 'INPUT_CHANGED'
const RADIO_CHANGED = 'RADIO_CHANGED'
const CLEAR_FORM = 'CLEAR_FORM'
const SET_SETTINGS = 'SET_SETTINGS'
const SET_PAYMENT_METHODS = 'SET_PAYMENT_METHODS'

const paymentForm = {
    builder: {
        address: null,
        city: null,
        state: null,
        postcode: null,
        country: null
    },
    payment_method: null,
    note_accepted: false,
}

/**
 * Vuex state for job module
 * @type {Object}
 */
const state = {
    paymentForm: {...paymentForm},
    paymentMethods: [],
    paycreteSettings: []
}

/**
 * Vuex mutations for job module
 * @type {Object}
 */
const mutations = {
    [SET_SETTINGS]: (state, payload) => {
        state.paycreteSettings = payload
    },

    [SET_PAYMENT_METHODS]: (state, payload) => {
        state.paymentMethods = payload
    },

    [INPUT_CHANGED]: (state, payload) => {
        const { key, value } = payload
        const stack = Object.assign({}, state.paymentForm)
        
        set(stack, key, value)
        state.paymentForm = stack
    },

    [RADIO_CHANGED]: (state, { value }) => {
        state.paymentForm.payment_method = value
    },

    [CLEAR_FORM]: (state) => {
        state.paymentForm = {...paymentForm}
        state.paymentForm.builder = {...paymentForm.builder}
    }
}

/**
 * Vuex actions for job module
 * @type {Object}
 */
const actions = {
    async fetchSettings({ commit }) {
        const { data } = await axios.post('/api/settings', {
            alias: 'paycrete'
        })
        commit(SET_SETTINGS, data.settings)
    },

    async fetchPaymentMethods({ commit }) {
        const { data } = await axios.get('/api/jobs/payments/retrieve-methods')
        commit(SET_PAYMENT_METHODS, data)
    },
    
    async archiveSubmitPayment({ dispatch }, payload) {
        const { 
            card, 
            app, 
            job, 
            stripe_card_id, 
            onFailed, 
            onSuccess, 
            bid_amount 
        } = payload

        try {
            const { data } = await axios.post(`/api/jobs/${job.uuid}/payments/charge`, { amount: bid_amount || job.total_amount_with_gst })

            if (stripe_card_id) {
                const stripeInstance = stripe.createStripeInstance()
                const result = await stripeInstance.confirmCardPayment(data.client_secret, { payment_method: stripe_card_id })

                if (result.error) {
                    if (onFailed instanceof Function) await onFailed(result.error)
                } else {
                    dispatch('storePayment', { 
                        job: job,  
                        stripe_payment_id: data.id,
                        stripe_card_id: stripe_card_id,
                        computed_amount: bid_amount || job.total_amount_with_gst,
                        paid_at: true
                    })
                    if (onSuccess instanceof Function) await onSuccess(result.paymentIntent)
                }
            } else {  
                const { paymentMethod } = await app.createPaymentMethod({
                    type: 'card',
                    card: card,
                    billing_details: {
                        name: job.builder.name,
                    },
                })

                const result = await app.confirmCardPayment(data.client_secret, { payment_method: paymentMethod.id })

                if (result.error) {
                    if (onFailed instanceof Function) await onFailed(result.error)
                } else {
                    if (onSuccess instanceof Function) await onSuccess(result.paymentIntent)
                }
            }
        } catch (e) {
            if (onFailed instanceof Function) await onFailed(e.response.data)
        }
    },

    async submitPayment({ dispatch }, payload) {
        const { 
            job, 
            stripe_card_id, 
            onFailed, 
            onSuccess, 
            bid_amount 
        } = payload

        try {
            if (stripe_card_id) {
                dispatch('storePayment', { 
                    job: job,  
                    stripe_payment_id: null,
                    stripe_card_id: stripe_card_id,
                    computed_amount: bid_amount || job.total_amount_with_gst,
                    paid_at: true
                })
            }
            if (onSuccess instanceof Function) await onSuccess()
        } catch (e) {
            if (onFailed instanceof Function) await onFailed(e.response.data)
        }
    },

    async savePaymentDetailsToStripe({ dispatch }, payload) {
        const { 
            card, 
            app, 
            job, 
            stripe_payment_id, 
            bid_amount, 
            onFailed, 
            onSuccess 
        } = payload
        
        if (app && card) {
            try {
                const { paymentMethod } = await app.createPaymentMethod({
                    type: 'card',
                    card: card,
                    billing_details: {
                        name: job.builder.name,
                    },
                })

                const { data } = await axios.post(`/api/jobs/${job.uuid}/payments/save`, { paymentMethodID: paymentMethod.id })

                dispatch('storePayment', { 
                    job: job,  
                    stripe_card_id: paymentMethod.id,
                    stripe_payment_id: stripe_payment_id,
                    computed_amount: bid_amount || job.total_amount_with_gst,
                    paid_at: true
                })

                if (onSuccess instanceof Function) await onSuccess(data.card)
            } catch (e) {
                if (onFailed instanceof Function) await onFailed(e.response.data)
            }
        } else {
            if (onSuccess instanceof Function) await onSuccess()
        }
    },

    async storePayment({ state }, payload) {
        const { paymentForm } = state
        const { 
            job, 
            paid_at, 
            stripe_payment_id, 
            stripe_card_id, 
            computed_amount,
            cycle_amount,
            bid_amount,
        } = payload

        let used_total_amount = computed_amount || job.total_amount_with_gst
        let used_bid_amount = bid_amount || 0
        let used_cycle_amount = cycle_amount || 0

        try {
            await axios.post(`/api/jobs/${job.uuid}/payments/store`, {
                status: "approved",
                payment_method: paymentForm.payment_method,
                bid_amount: parseFloat(used_bid_amount),
                total_amount: used_total_amount.toString(),
                cycle_amount: used_cycle_amount.toString(),
                stripe_payment_id: stripe_payment_id,
                stripe_card_id: stripe_card_id,
                paid_at: paid_at
            })
        } catch(e) {
            throw e.response.data
        }
    },

    clearForm({ commit }) {
        commit(CLEAR_FORM)
    },

    inputChanged({ commit }, payload) {
        const { key, value } = payload
        commit(INPUT_CHANGED, { key, value })
    },

    radioChanged({ commit }, { key, value }) {
        commit(RADIO_CHANGED, { key, value })
    }
}

/**
 * Vuex getters for job module
 * @type {Object}
 */
const getters = {
    paymentForm: state => state.paymentForm,
    paycreteSettings: state => state.paycreteSettings,
    paymentMethods: state => state.paymentMethods,
    ccCardsMethod: state => state.paymentMethods.ccCards,
    accountFormMethod: state => state.paymentMethods.accountForm,
    othersMethod: state => state.paymentMethods.others,
    directContactMethod: state => state.paymentMethods.directContact,
    directDepositMethod: state => state.paymentMethods.directDeposit,
    paycreteMethod: state => state.paymentMethods.paycrete,
    gst: state => parseFloat(state.paycreteSettings.find(setting => setting.alias === "paycrete_gst").value),
    fee_markup: state => parseFloat(state.paycreteSettings.find(setting => setting.alias === "paycrete_fee_markup").value),
    bsb: state => state.paycreteSettings.find(setting => setting.alias === "paycrete_bsb").value,
    account_number: state => state.paycreteSettings.find(setting => setting.alias === "paycrete_account_number").value,
    delivery_fee: state => parseFloat(state.paycreteSettings.find(setting => setting.alias === "paycrete_delivery_fee").value),
    delivery_fee_disclaimer: state => state.paycreteSettings.find(setting => setting.alias === "paycrete_delivery_fee_disclaimer").value,
    payment_cycles: state => parseInt(state.paycreteSettings.find(setting => setting.alias === "paycrete_payment_cycles").value),
    payment_duration: state => parseInt(state.paycreteSettings.find(setting => setting.alias === "paycrete_payment_duration").value),
    paycrete_disclaimer: state => state.paycreteSettings.find(setting => setting.alias === "paycrete_disclaimer").value,
    paycrete_enabled: state => state.paycreteSettings.find(setting => setting.alias === "paycrete_enabled").is_enabled,
    paycrete_credit_limit: state => state.paycreteSettings.find(setting => setting.alias === "paycrete_credit_limit").value,
    paycrete_terms_and_conditions: state => state.paycreteSettings.find(setting => setting.alias === "paycrete_terms_and_conditions").value,
    paycrete_privacy_policy: state => state.paycreteSettings.find(setting => setting.alias === "paycrete_privacy_policy").value,
}

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters
}
